import {
  Box,
  Button,
  Dialog,
  Heading,
  HStack,
  Image,
  Portal,
  Text,
  VStack,
  useBreakpointValue,
} from '@chakra-ui/react';
import { RiftboundBottomSheet, SHEET_Z_FILTER } from './ui/bottom-sheet';
import { SupportDonationButton } from './SupportDonationButton';
import { markSupportModalShown } from '../utils/supportModalTracking';

interface SupportModalProps {
  open: boolean;
  onClose: () => void;
}

/** Desktop dialog — no tab bar to worry about. */
const SUPPORT_DIALOG_Z = 1500;

const WORDMARK_URL = `${import.meta.env.BASE_URL}riftbound-db-logo-gold.png`;

function SupportModalLogo({ size }: { size: number }) {
  return (
    <Box
      animation="bounce 2s ease-in-out infinite"
      css={{
        '@keyframes bounce': {
          '0%, 100%': { transform: 'translateY(0)' },
          '50%': { transform: 'translateY(-8px)' },
        },
      }}
    >
      <Image
        src={WORDMARK_URL}
        alt="Riftbound DB"
        h={`${size}px`}
        w="auto"
        maxW="100%"
        objectFit="contain"
      />
    </Box>
  );
}

function SupportModalContent({
  onClose,
  isMobile,
}: {
  onClose: () => void;
  isMobile: boolean;
}) {
  return (
    <VStack gap={4} align="center" textAlign="center" py={2}>
      {!isMobile && (
        <>
          <SupportModalLogo size={48} />
          <Heading as="h1" size="2xl" color="text" fontWeight="black">
            Support Riftbound DB
          </Heading>
        </>
      )}
      <Text color="text" opacity={0.8} maxW="320px" lineHeight="tall">
        Enjoying Riftbound DB? This project is built with love and maintained independently. Your
        support helps keep it running and improving!
      </Text>

      <Box display="flex" justifyContent="center" w="100%">
        <SupportDonationButton />
      </Box>

      <Button
        variant="ghost"
        size="sm"
        onClick={onClose}
        color="text"
        opacity={0.6}
        _hover={{ opacity: 1 }}
        fontWeight="normal"
      >
        Maybe later
      </Button>
    </VStack>
  );
}

export function SupportModal({ open, onClose }: SupportModalProps) {
  const isMobile = useBreakpointValue({ base: true, md: false });

  const handleClose = () => {
    markSupportModalShown();
    onClose();
  };

  if (!open) return null;

  if (isMobile) {
    return (
      <RiftboundBottomSheet
        isOpen={open}
        onClose={handleClose}
        zIndex={SHEET_Z_FILTER}
        initialSnap={1}
        detent="content-height"
        header={
          <HStack justify="space-between" align="center" w="100%" px={4} py={2}>
            <SupportModalLogo size={32} />
            <Heading as="h2" size="lg" color="text" fontWeight="black">
              Support Riftbound DB
            </Heading>
            <Button variant="ghost" size="sm" onClick={handleClose} p={2} minW="auto">
              ✕
            </Button>
          </HStack>
        }
      >
        <Box px={4} pb={4}>
          <SupportModalContent isMobile={isMobile} onClose={handleClose} />
        </Box>
      </RiftboundBottomSheet>
    );
  }

  return (
    <Dialog.Root
      open={open}
      size="sm"
      placement="center"
      onOpenChange={(e) => {
        if (!e.open) handleClose();
      }}
    >
      <Portal>
        <Dialog.Backdrop bg="blackAlpha.700" style={{ backdropFilter: 'blur(3px)' }} />
        <Dialog.Positioner>
          <Dialog.Content
            style={{
              backgroundColor: 'transparent',
              border: 0,
              padding: 0,
              margin: 0,
              boxShadow: 'none',
            }}
          >
            <Dialog.Body
              display="flex"
              flexDirection="column"
              p={6}
              bg="bg"
              borderRadius="xl"
              border="hairline"
              borderColor="border"
              w="100%"
              minW="360px"
              position="relative"
              style={{ zIndex: SUPPORT_DIALOG_Z }}
            >
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClose}
                position="absolute"
                top={3}
                right={3}
                p={2}
                minW="auto"
              >
                ✕
              </Button>

              <SupportModalContent isMobile={false} onClose={handleClose} />
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
}
